import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(2)
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 400,
    width: '100%',
    textAlign: 'center'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  submitButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    }
  },
  title: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    fontWeight: 'bold'
  },
  link: {
    marginTop: theme.spacing(2),
    textAlign: 'center'
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  benefits: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    width: '100%'
  }
}));

function AffiliateLogin() {
  const classes = useStyles();
  const history = useHistory();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use full backend URL or relative if proxied
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://servermain.sisrevolution.com';
      const response = await fetch(`${API_BASE_URL}/api/affiliates/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        // Store token and user data
        localStorage.setItem('affiliateToken', data.token);
        localStorage.setItem('affiliateUser', JSON.stringify(data.user));
        
        // Redirect to dashboard
        history.push('/affiliate/dashboard');
      } else {
        setError(data.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className={classes.container}>
      <Paper className={classes.paper} elevation={3}>
        <Typography variant="h4" className={classes.title}>
          Affiliate Login
        </Typography>
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Access your affiliate dashboard to track referrals and earnings
        </Typography>

        {error && (
          <Paper style={{ padding: 16, backgroundColor: '#ffebee', color: '#c62828', marginBottom: 16 }}>
            <Typography variant="body1">{error}</Typography>
          </Paper>
        )}

        <form onSubmit={handleSubmit} className={classes.form}>
          <TextField
            name="email"
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            fullWidth
            variant="outlined"
          />
          
          <TextField
            name="password"
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            required
            fullWidth
            variant="outlined"
          />
          
          <Button
            type="submit"
            variant="contained"
            className={classes.submitButton}
            disabled={loading}
            fullWidth
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
          </Button>
        </form>

        <Divider className={classes.divider} />

        <Box className={classes.link}>
          <Typography variant="body2">
            Don't have an account?{' '}
            <RouterLink 
              to="/affiliate/register" 
              style={{ color: '#667eea', textDecoration: 'none', cursor: 'pointer' }}
            >
              Sign up here
            </RouterLink>
          </Typography>
        </Box>

        <Box className={classes.benefits}>
          <Typography variant="h6" style={{ marginBottom: 8 }}>
            💰 Affiliate Benefits:
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 4 }}>
            • Earn 100 GHS per successful school referral
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 4 }}>
            • Commission paid after school uses the system for a term
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 4 }}>
            • Track all your referrals in real-time
          </Typography>
          <Typography variant="body2">
            • Get paid via mobile money (minimum payout: GHS 700)
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}

export default AffiliateLogin;
